import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-location';
import { isFunction } from 'lodash-es';
import { getLocationPortalDetails } from '@frontend/api-weave-pay';
import { HttpError } from '@frontend/fetch';
import { useTranslation } from '@frontend/i18n';
import { useQuery } from '@frontend/react-query-helpers';
import { genUUIDV4 } from '@frontend/string';
import { FailureHandler, PortalDataType } from '../types';
import { getProcessor } from '../utils';

/**
 * Hook that returns necessary data from query for Online Bill Pay (OBP) Portal
 * OBP is an anonymous payment solution for offices
 * @param {string} locationSlug unique location Id associated with online bill pay
 * @param {function} onFailure function to be called on request failure
 *
 * @returns {PortalDataType}
 * @property {number} amount - undefined for OBP
 * @property {string} attachment - undefined for OBP
 * @typedef {Object} locationData - locationData such as email, id, logo, name, phoneNumber, and activeProcessor,
 * @property {boolean} locationData.isPortalEnabled - Whether the portal is enabled, always true for TTP
 * @property {boolean} locationData.newPortalEnabled - Whether the feature flag payments:weave-pay-refactor is on or off
 * @property {boolean} locationData.isLive - Whether the account is using Live keys or Test keys
 * @property {string} onBehalfOf - processor accountId,
 * @property {string} requestId - unique Id for use with payment submission. LocationId for OBP
 * @property {string} setupIntentId - payment Id for the request, tied to the payment intent, changes if a new payment attempt occurs. OBP generates its own
 * @typedef {Object} person - name, personId, and boolean for hasBirthdate for attachment auth
 * @typedef {Object} settings - boolean settings for state management
 * @property {boolean} settings.allowSaveACHOnFile - can save ACH
 * @property {boolean} settings.allowSaveCardOnFile - can save Card
 * @property {boolean} settings.oneTimeRequest - if the request is one time use, always true for TTP
 * @property {boolean} settings.showExpressElement - whether to show the Express Payment Element, always true for TTP
 * @property {boolean} settings.showAttachment - whether do show attachment
 * @property {boolean} settings.skipAttachmentAuth - whether the setting is enabled to require auth on attachment view
 */

interface UseLocationPortalParams {
  locationSlug: string;
  onFailure?: FailureHandler;
}

export const useLocationPortal = ({ locationSlug, onFailure }: UseLocationPortalParams) => {
  const navigate = useNavigate();
  const { t } = useTranslation('weave-pay');

  const { data: portalData, refetch: refetchPortalData } = useQuery({
    queryKey: [locationSlug],
    queryFn: () => getLocationPortalDetails(locationSlug),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    select: (data): PortalDataType => ({
      attachment: data.links.attachment,
      locationData: {
        email: data.location?.email,
        id: data.location?.id,
        isPortalEnabled: data.onlineBillPayEnabled,
        isLive: data.location.isLive,
        logo: data.links.locationLogo,
        name: data.location.name,
        newPortalEnabled: data.weavePayRefactorEnabled,
        phoneNumber: data.location.phoneNumber,
        processor: getProcessor(data.currentProcessor),
      },
      onBehalfOf: data.onBehalfOf,
      requestId: data.location.id,
      setupIntentId: genUUIDV4(),
      settings: {
        allowSaveACHOnFile: false,
        allowSaveCardOnFile: false,
        oneTimeRequest: false,
        showExpressElement: false,
        showAttachment: false,
        skipAttachmentAuth: false,
      },
    }),
    onError: (err) => {
      if (!isFunction(onFailure)) navigate({ to: '/error' });
      else if (err instanceof HttpError) onFailure(err);
    },
  });

  useEffect(() => {
    if (portalData?.requestId && !portalData?.onBehalfOf) {
      onFailure?.(new Error(t('empty onBehalfOf')));
    }
  }, [portalData]);

  return { portalData, newPortalEnabled: portalData?.locationData?.newPortalEnabled, refetchPortalData };
};
